import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ExchangeRateService {
  private http = inject(HttpClient);
  
  // Bank of Canada API endpoint
  // Common Series: 
  // FXUSDCAD = USD to CAD
  private baseUrl = 'https://www.bankofcanada.ca/valet/observations';

  /**
   * Fetches the latest available exchange rate for the specified month.
   * It queries the entire month to find the last business day's rate.
   * 
   * @param period Format "YYYY/MM" (e.g., "2024/01")
   * @param series The currency pair series (default 'FXUSDCAD')
   */
  async getLatestOfMonthRate(period: string, series: string = 'FXUSDCAD'): Promise<{ rate: number, date: string } | null> {
    const [yearStr, monthStr] = period.split('/');
    const year = parseInt(yearStr);
    const month = parseInt(monthStr); // 1-based
    
    
    // Construct date range for the entire month
    const startDate = `${yearStr}-${monthStr.padStart(2, '0')}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const endDate = `${yearStr}-${monthStr.padStart(2, '0')}-${lastDay}`;

    const url = `${this.baseUrl}/${series}/json?start_date=${startDate}&end_date=${endDate}`;

    try {
      const response: any = await firstValueFrom(this.http.get(url));
      
      if (response && response.observations && response.observations.length > 0) {
        // Observations are sorted by date. The last one is the latest business day.
        const lastObservation = response.observations[response.observations.length - 1];
        return {
          rate: parseFloat(lastObservation[series].v),
          date: lastObservation.d
        };
      }
      return null;
    } catch (error) {
      console.error(`Failed to fetch exchange rate for ${series}:`, error);
      return null;
    }
  }

  /**
   * Fetches the exchange rate from Canadian Dollar to US Dollar (1 CAD = X USD).
   * Since the API provides USD to CAD (FXUSDCAD), this method fetches that and returns the inverse.
   */
  async getCADtoUSDRate(period: string): Promise<{ rate: number, date: Date } | null> {
    const data = await this.getLatestOfMonthRate(period, 'FXUSDCAD');
    if (data) {
      return {
        rate: 1 / data.rate,
        date: new Date(data.date)
      };
    }
    return null;
  }
}